import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SpinnerService } from '@core/services/spinner.service';
import { ModalFactory } from '@yourcause/common/modals';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { ManageTagsModalComponent } from '../manage-tags-modal/manage-tags-modal.component';
import { SystemTagsService } from '../system-tags.service';
import { SystemTags } from '../typings/system-tags.typing';

@Component({
    selector: 'gc-manage-tags',
    templateUrl: './manage-tags.component.html',
    styleUrls: ['./manage-tags.component.scss'],
    standalone: false
})
export class ManageTagsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tagType: SystemTags.Buckets;
  @Input() subTagType: SystemTags.Buckets;
  @Input() entityId: string|number;
  @Input() subEntityId: string|number;
  @Input() isNomination = false;
  @Input() showManageLink = true;
  @Output() onTagsUpdated = new EventEmitter();

  manageLinkVisible: boolean;
  currentTags: SystemTags.TagDefinitionForUi[];
  currentTagIds: number[];
  currentSubTagIds: number[];
  currentTagsOnMap: number[];
  isAfterInit = false;
  sub = new Subscription();

  constructor (
    private spinnerService: SpinnerService,
    private systemTagsService: SystemTagsService,
    private modalFactory: ModalFactory,
    private arrayHelper: ArrayHelpersService
  ) {
    this.systemTagsService.changesTo$('tagMap').subscribe(() => {
      if (this.isAfterInit) {
        const mapTagIds = this.systemTagsService.getTagsForRecord(
          this.tagType,
          this.entityId
        );
        if (!!mapTagIds) {
          // check to see if tags are different than current
          const mappedTagIds = this.arrayHelper.sort(mapTagIds);
          const currentTagIds = this.arrayHelper.sort(this.currentTagIds);
          if (!isEqual(mappedTagIds, currentTagIds)) {
            this.prepareTags();
          }
        }
      }
    });
  }

  async ngOnInit () {
    await this.systemTagsService.resolveSystemTags();
    await this.prepareTags();
    this.manageLinkVisible = this.showManageLink && (
      (this.systemTagsService.getTagsForBucket(this.tagType).length > 0) ||
      (this.subTagType ? this.systemTagsService.getTagsForBucket(this.subTagType).length > 0 : false)
    );
    this.isAfterInit = true;
  }

  async ngOnChanges (changes: SimpleChanges) {
    if (this.isAfterInit) {
      if (changes.entityId || changes.subEntityId) {
        await this.prepareTags();
      }
    }
  }

  async prepareTags () {
    await this.setCurrentTags();
    this.currentTags = this.currentTagIds.map((id) => {
      return this.systemTagsService.allTags.find(tag => tag.id === id);
    });

    if (this.subEntityId && this.subTagType) {
      await this.setCurrentTags(true);

      this.currentTags = this.currentTags.concat(this.currentSubTagIds.map((id) => {
        return this.systemTagsService.allTags.find(tag => tag.id === id);
      }));
    }
  }

  async setCurrentTags (isSubTag = false) {
    const tagIdsAttr: keyof ManageTagsComponent = isSubTag ? 'currentSubTagIds' : 'currentTagIds';
    const tagTypeAttr: keyof ManageTagsComponent = isSubTag ? 'subTagType' : 'tagType';
    const entityAttr: keyof ManageTagsComponent = isSubTag ? 'subEntityId' : 'entityId';
    this[tagIdsAttr] = this.systemTagsService.getTagsForRecord(
      this[tagTypeAttr],
      this[entityAttr]
    );
    if (!this[tagIdsAttr]) {
      await this.systemTagsService.fetchTagsForRecord(this[tagTypeAttr], this[entityAttr]);
      this[tagIdsAttr] = this.systemTagsService.getTagsForRecord(
        this[tagTypeAttr],
        this[entityAttr]
      );
    }
  }

  async manageTags () {
    const tagIds = await this.modalFactory.open(
      ManageTagsModalComponent,
      {
        bucket: this.tagType,
        subBucket: this.subTagType,
        currentTagIds: this.currentTagIds.concat(this.currentSubTagIds ?? []),
        isNomination: this.isNomination,
        ids: [+this.entityId]
      }
    );
    if (tagIds) {
      this.spinnerService.startSpinner();
      const {
        newTags,
        removedTags
      } = this.systemTagsService.getNewAndRemovedTags(
        this.currentTags,
        tagIds
      );
      await this.systemTagsService.setTagsOnRecord(
        this.tagType,
        this.entityId,
        newTags,
        removedTags,
        this.subTagType,
        this.subEntityId
      );
      this.onTagsUpdated.emit();
      this.ngOnInit();
      this.spinnerService.stopSpinner();
    }
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
